'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SeatData } from '@/components/grid';

/**
 * Resolve the bulk-action category a given seat falls into.
 *
 * Mirrors `useMultiSelect.getSeatMode` (`@/hooks/use-multi-select.ts:103-109`)
 * — kept in sync byte-for-byte so the basket and the bulk-action drawer
 * agree on which mode each seat belongs to. Forfeit-pending takes
 * precedence over ownership: an owned seat whose deposit has decayed to
 * zero has nothing left to "manage", and the next contract transition on
 * it is the forfeit itself, which any caller registers via `pokeTax` —
 * routing those seats to `'poke'` keeps the basket category coherent.
 */
/**
 * Bulk-action category resolved per seat by `getBasketSeatMode`.
 * Mirrors `useMultiSelect.BulkMode` byte-for-byte (including the
 * `'manage-creator'` branch) so the basket and the bulk drawer agree
 * on which tiles can co-exist in a single basket. See
 * `use-multi-select.ts::BulkMode` JSDoc for the full taxonomy.
 */
export type BulkMode = 'manage' | 'manage-creator' | 'poke' | 'claim' | 'buyout';

export function getBasketSeatMode(seat: SeatData): BulkMode {
  if (seat.isReserved) return 'claim';
  if (seat.isPendingForfeit) return 'poke';
  // Creator inventory (viewer == grid creator, holder == viewer,
  // deposit == 0n, pre-graduation) is its own basket category. This
  // is what stops a creator from accidentally mixing their primary-
  // mint inventory with seats they later bought back from a third
  // party — those two cohorts have completely different action
  // surfaces (creator inventory has no Harberger deposit to manage,
  // bought-back seats do) and the bulk drawer can't render them in
  // the same body without misleading the user. The bulk drawer
  // routes this mode to a "share grid link" notice mirroring the
  // single-tile BuyOutDrawer's creator branch.
  if (seat.isOwn && seat.isCreatorSeat) return 'manage-creator';
  if (seat.isOwn) return 'manage';
  if (!seat.holder || seat.isForfeited || seat.isCreatorSeat) return 'claim';
  return 'buyout';
}

/**
 * `true` iff the seat is currently sitting inside an active V22 Dutch
 * auction window — i.e. the grid uses Dutch decay, the seat is vacant
 * (no holder or forfeited), and `antiSnipeEndsAt` is still in the
 * future. Held seats on Dutch grids and post-window-expiry vacant
 * seats (free-claim phase) both return `false`.
 *
 * Bulk Dutch flows would surface a per-seat decay clock × N rows in
 * the bulk drawer with mismatched clearing prices and refund maths,
 * which the user explicitly called out as too confusing. The basket
 * refuses to add seats matching this predicate (see `toggle` below) so
 * Dutch buyouts always go through the single-tile `BuyOutDrawer`
 * routed by `LobbyClient#openDrawerForSeat`. Every other tile on the
 * same Dutch grid (held seats, expired-window vacant seats) remains
 * freely multi-selectable.
 */
export function isSeatInActiveDutchAuction(seat: SeatData): boolean {
  if (!seat.gridUsesDutchAuction) return false;
  if (seat.holder && !seat.isForfeited) return false;
  if (!seat.antiSnipeEndsAt) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return seat.antiSnipeEndsAt > nowSec;
}

/**
 * Lobby-local multi-select "basket" for Cmd/Ctrl+click checkout flow.
 *
 * Feature scope (matches the classic app's `useMultiSelect` hook at a high
 * level but intentionally trimmed for the lobby preview):
 *   - Cmd/Ctrl+click a tile → toggle it in the basket
 *   - When the basket is non-empty, any click (with or without the modifier)
 *     toggles membership — the user stays in "basket mode" until the basket
 *     is cleared (Esc / Clear / Checkout).
 *   - The basket is **single-mode**: once a seat is added, only seats
 *     resolving to the same `BulkMode` (manage / buyout / claim / poke)
 *     are accepted by `toggle`. Mismatched clicks no-op so a user can't
 *     end up with a basket that mixes Manage and Buyout (the user-
 *     reported regression). The mode resets back to `null` whenever the
 *     basket empties (clear / last toggle removes the only entry).
 *   - Tiles that match `isSeatInActiveDutchAuction` are silently
 *     refused by `toggle`. Single-tile Dutch buyouts continue to work
 *     through the standard `LobbyClient#openDrawerForSeat` route —
 *     this just keeps them out of the multi-select basket. Every
 *     other tile on the same Dutch grid (held seats, expired-window
 *     vacant seats) is still freely multi-selectable.
 *
 * Note: we listen for `Meta` (⌘ on macOS), `Control` (Ctrl on
 * Windows/Linux) and `Shift` so the hint state lights up for any of the
 * three. Shift is offered as an equal alternative (the classic app's
 * convention) — it doesn't collide with anything else on the board: the
 * 3D camera's `orbitMouseButtons` never binds it, and the 2D board has no
 * other Shift gesture.
 */
export interface LobbyBasket {
  /** Seat indices currently in the basket. */
  selectedIndices: Set<number>;
  /** Active bulk-action category, or `null` while the basket is empty.
   *  Drives the lobby-canvas dim/focus overlay (non-matching tiles drop
   *  to ~30% opacity) and gates the bulk-action drawer's mode. */
  bulkMode: BulkMode | null;
  /** True while the user is actively holding Cmd (macOS), Ctrl
   *  (Windows/Linux) or Shift. Drives hover-time cursor / outline hints
   *  even before the first toggle. */
  isModifierHeld: boolean;
  /** Add or remove a seat. Toggling a seat whose mode doesn't match the
   *  current basket mode is a no-op — the basket is single-mode by
   *  construction. Adds for tiles in an active Dutch auction window
   *  are also refused (single-tile buyout only). The boolean
   *  return is `true` when the basket changed, `false` when the
   *  click was rejected (handy for the canvas's "click feedback"
   *  hooks that should suppress the toggle ping when we ignored
   *  the click). */
  toggle: (seat: SeatData) => boolean;
  /** Remove a single seat from the basket (no-op if absent). */
  remove: (seatIndex: number) => void;
  /** Additively merge a batch of seats into the basket in a single
   *  state update — the commit path for the 2D canvas's Cmd/Ctrl+drag
   *  marquee. Never removes (so dragging a second box *stacks* on the
   *  current selection rather than toggling overlaps off) and honours
   *  the same single-mode + Dutch-auction gates as `toggle`: the first
   *  eligible seat in the batch establishes the basket mode when the
   *  basket is empty; seats whose mode doesn't match (or that sit in an
   *  active Dutch window) are skipped. Already-basketed seats in the
   *  batch are left untouched. No-op when nothing in `seats` is
   *  eligible. */
  selectMany: (seats: SeatData[]) => void;
  /** Replace the basket with *exactly* this set of seats in a single
   *  state update — the commit path for the floor-sweep slider, whose
   *  count goes both up and down as the user scrubs (so additive
   *  `selectMany` can't model it). Honours the same single-mode +
   *  Dutch-auction gates as `toggle`/`selectMany`: the first eligible
   *  seat establishes the mode and any seat whose mode doesn't match (or
   *  that sits in an active Dutch window) is dropped from the result. An
   *  empty / all-ineligible input clears the basket (mode → null). */
  selectExactly: (seats: SeatData[]) => void;
  /** Empty the basket. Called by Esc, Clear button, post-checkout, and
   *  on every board-change so a stale basket never bleeds into the
   *  next board's first click. */
  clear: () => void;
  /** Pure helper: which mode would this seat fall into? Re-exported so
   *  the canvases can compute "is this tile dimmed / selectable?"
   *  without duplicating the precedence rules. */
  getSeatMode: (seat: SeatData) => BulkMode;
  /** `true` when the seat is either the basket's first entry (any mode
   *  is accepted) or matches the basket's current mode. Mirrors the
   *  classic `useMultiSelect.isSeatSelectable` semantics. Returns
   *  `false` for tiles that are currently in an active Dutch auction
   *  window — those open through the single-tile buyout drawer
   *  instead. */
  isSeatSelectable: (seat: SeatData) => boolean;
}

export function useLobbyBasket(): LobbyBasket {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    () => new Set<number>(),
  );
  const [bulkMode, setBulkMode] = useState<BulkMode | null>(null);
  const [isModifierHeld, setIsModifierHeld] = useState(false);

  useEffect(() => {
    const isBasketMod = (e: KeyboardEvent) =>
      e.key === 'Meta' || e.key === 'Control' || e.key === 'Shift';
    const onKeyDown = (e: KeyboardEvent) => {
      if (isBasketMod(e)) setIsModifierHeld(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (isBasketMod(e)) setIsModifierHeld(false);
    };
    // If the user tabs away or the window loses focus we might miss the
    // keyup (particularly on macOS where Cmd+Tab eats the event).
    const onBlur = () => setIsModifierHeld(false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const toggle = useCallback((seat: SeatData): boolean => {
    const seatMode = getBasketSeatMode(seat);
    let changed = false;
    setSelectedIndices((prev) => {
      // Removal path: always allowed, regardless of current mode (a
      // non-matching seat couldn't have entered the basket anyway, but
      // we don't want a stale-mode click to leak past the membership
      // check). Also bypasses the Dutch-auction refusal below so an
      // already-basketed tile can still be removed even if its window
      // expired mid-session.
      if (prev.has(seat.seatIndex)) {
        const next = new Set(prev);
        next.delete(seat.seatIndex);
        if (next.size === 0) setBulkMode(null);
        changed = true;
        return next;
      }

      // Addition path: first pick sets the mode; subsequent picks must
      // match. Mismatched clicks no-op (return `prev` so React skips
      // the re-render entirely).
      if (bulkMode != null && seatMode !== bulkMode) {
        return prev;
      }
      // Tiles inside an active Dutch auction window are not
      // basket-eligible — the per-seat decay clock + clearing-price
      // maths don't compose into a bulk drawer. The single-tile
      // buyout drawer (`BuyOutDrawer` via `openDrawerForSeat`)
      // handles those. Every other tile on the same Dutch grid
      // (held seats for buyout, expired-window vacant seats for
      // claim) stays freely multi-selectable.
      if (isSeatInActiveDutchAuction(seat)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(seat.seatIndex);
      if (bulkMode == null) setBulkMode(seatMode);
      changed = true;
      return next;
    });
    return changed;
  }, [bulkMode]);

  const remove = useCallback((seatIndex: number) => {
    setSelectedIndices((prev) => {
      if (!prev.has(seatIndex)) return prev;
      const next = new Set(prev);
      next.delete(seatIndex);
      if (next.size === 0) setBulkMode(null);
      return next;
    });
  }, []);

  const selectMany = useCallback(
    (seats: SeatData[]) => {
      if (seats.length === 0) return;
      setSelectedIndices((prev) => {
        // First eligible seat sets the mode when the basket is empty;
        // otherwise the established basket mode stands. Mirrors
        // `toggle`'s single-mode contract so a marquee can never mix
        // Manage + Buyout (the same regression the click path guards).
        let mode = bulkMode;
        const next = new Set(prev);
        let changed = false;
        for (const seat of seats) {
          // Already basketed — leave it (additive, never toggles off).
          if (next.has(seat.seatIndex)) continue;
          // Active Dutch window → single-tile buyout only (see `toggle`).
          if (isSeatInActiveDutchAuction(seat)) continue;
          const seatMode = getBasketSeatMode(seat);
          if (mode == null) mode = seatMode;
          else if (seatMode !== mode) continue;
          next.add(seat.seatIndex);
          changed = true;
        }
        if (!changed) return prev;
        if (bulkMode == null && mode != null) setBulkMode(mode);
        return next;
      });
    },
    [bulkMode],
  );

  const selectExactly = useCallback((seats: SeatData[]) => {
    // Resolve the eligible set + its single mode up front (pure, no
    // dependency on the previous selection — this is a *replace*, not a
    // merge). The first eligible seat fixes the mode; later seats that
    // disagree, or that sit in an active Dutch window, are dropped.
    let mode: BulkMode | null = null;
    const next = new Set<number>();
    for (const seat of seats) {
      if (isSeatInActiveDutchAuction(seat)) continue;
      const seatMode = getBasketSeatMode(seat);
      if (mode == null) mode = seatMode;
      else if (seatMode !== mode) continue;
      next.add(seat.seatIndex);
    }
    setSelectedIndices((prev) => {
      // Bail on a no-op replace (same membership) so React skips the
      // re-render — matters because the sweep slider can fire this on
      // every pointermove tick while the resolved set is unchanged.
      if (prev.size === next.size) {
        let same = true;
        for (const i of next) {
          if (!prev.has(i)) {
            same = false;
            break;
          }
        }
        if (same) return prev;
      }
      return next;
    });
    setBulkMode(next.size === 0 ? null : mode);
  }, []);

  const clear = useCallback(() => {
    setSelectedIndices((prev) => (prev.size === 0 ? prev : new Set<number>()));
    setBulkMode(null);
  }, []);

  const getSeatMode = useCallback(
    (seat: SeatData): BulkMode => getBasketSeatMode(seat),
    [],
  );

  const isSeatSelectable = useCallback(
    (seat: SeatData): boolean => {
      // Already-selected tiles always read as selectable so the
      // toggle's removal path keeps registering even if the seat's
      // Dutch window opens up after it was added.
      if (selectedIndices.has(seat.seatIndex)) return true;
      if (isSeatInActiveDutchAuction(seat)) return false;
      return bulkMode == null || getBasketSeatMode(seat) === bulkMode;
    },
    [bulkMode, selectedIndices],
  );

  return useMemo(
    () => ({
      selectedIndices,
      bulkMode,
      isModifierHeld,
      toggle,
      remove,
      selectMany,
      selectExactly,
      clear,
      getSeatMode,
      isSeatSelectable,
    }),
    [
      selectedIndices,
      bulkMode,
      isModifierHeld,
      toggle,
      remove,
      selectMany,
      selectExactly,
      clear,
      getSeatMode,
      isSeatSelectable,
    ],
  );
}
